import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

// Dev-only UI mode: lets the interface run without the backend so screens can
// be built and reviewed. Requires REACT_APP_MOCK_AUTH=true in a local env file
// (e.g. .env.development.local, which is gitignored) and never activates in
// production builds.
const MOCK_AUTH =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_MOCK_AUTH === 'true';

const MOCK_USER = {
  _id: 'mock-user',
  name: 'Josue Fuentes',
  email: 'jafuentes@skylinecoho.com',
  role: 'admin',
};

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthStatus = async () => {
      if (MOCK_AUTH) {
        console.log('MOCK_AUTH enabled — using mock admin user (dev only)');
        setUser(MOCK_USER);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      const tokenInfo = authService.checkToken();
      if (!tokenInfo.tokenInLocalStorage || tokenInfo.isExpired) {
        if (tokenInfo.isExpired) {
          authService.logout();
        }
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const userData = await authService.getCurrentUserProfile();
        if (userData) {
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          authService.logout();
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error.response?.status || error.message);
        authService.logout();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    if (MOCK_AUTH) {
      setUser(MOCK_USER);
      setIsAuthenticated(true);
      setIsLoading(false);
      return { success: true };
    }

    try {
      setIsLoading(true);
      const response = await authService.login(email, password);

      if (!response.token) {
        return {
          success: false,
          error: 'Authentication failed - no token received',
        };
      }

      const userData = await authService.getCurrentUserProfile();
      setUser(userData);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error.response?.status || error.message);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed. Please check your credentials.',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    navigate('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
